import { cssMap as unboundedCssMap, keyframes } from '@compiled/react';
import type { ComponentType, FC, ReactNode } from 'react';
import React, { useCallback, useRef } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';

import LinkItem from '@atlaskit/menu/link-item';
import { Box, Grid, Inline } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import NodeIcon from '@atlaskit/icon/core/node';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent';
import { isValidEmojiOrFavicon } from '@confluence/emoji-title/entry-points/isValidEmojiOrFavicon';
import { ContentTreeIconLoader as ContentTreeIcon } from '@confluence/icons/entry-points/ContentTreeIcon';
import type { ContentTreeTypes } from '@confluence/icons/entry-points/contentTreeTypes';
import { includesValidContentTreeType } from '@confluence/icons/entry-points/contentTreeTypes';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { ConditionalTreeItemTooltip } from '@confluence/page-tree/entry-points/ConditionalTreeItemTooltip';
import { ConditionalInlineRename } from '@confluence/page-tree/entry-points/InlineRename';
import type { IconItemType } from '@confluence/page-tree/entry-points/PageTreeAfterIcon';
import { PageTreeAfterIcon } from '@confluence/page-tree/entry-points/PageTreeAfterIcon';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';

type SpaceViewsListItemProps = {
	title: string;
	url: string;
	onItemClick?: (id: string, index?: number) => void;
	isSelected: boolean;
	item: IconItemType;
	spaceKey: string;
	isSuperAdmin: boolean;
	highlight?: boolean;
	displayEmoji?: string;
	contentType?: string;
	isHoverPageCardOptedIn?: boolean;
	closeAllHoverPageCards?: () => void;
	setOpenQuickActionsId: (state: string | null) => void;
	isQuickActionsOpen: boolean;
	isQuickActionsFocused: boolean;
	setFocusedQuickActionsId: (focusedQuickActionsId: string | null) => void;
	isEditingTitleId: string | null;
	setIsEditingTitleId: (isEditingTitleId: string | null) => Promise<void>;
	onEnterHoverTarget?: (params: {
		itemID: string;
		isQuickActionsFocused: boolean;
		hoverPageCardTriggerRef: React.RefObject<HTMLDivElement>;
	}) => void;
	onLeaveHoverTarget?: () => void;
	onBlurHoverTarget?: (e) => void;
	shouldRenderAfterIcon?: boolean;
	index?: number;
	renderTitle?: ({ title }: { title: string }) => ReactNode;
};

const styles = cssMap({
	emojiWrapper: {
		display: 'flex',
		flexShrink: 0,
		alignItems: 'center',
		marginRight: token('space.075'),
	},
	nav4EmojiWrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: 0,
	},
	title: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	contentIconWrapper: {
		display: 'flex',
		alignItems: 'center',
		paddingRight: token('space.075'),
	},
	contentIconWrapperNav4: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: 0,
	},
	grid: {
		alignItems: 'center',
		columnGap: token('space.075'),
		gridTemplateColumns: 'minmax(0, auto) 1fr',
	},
});

const EmojiOrContentIcon = ({
	isContentTreeIconVisible,
	contentType,
	iconColor,
	displayEmoji,
	isNav4Enabled = false,
}) => {
	if (isContentTreeIconVisible && !displayEmoji) {
		return (
			<Box
				as="span"
				xcss={isNav4Enabled ? styles.contentIconWrapperNav4 : styles.contentIconWrapper}
			>
				<ContentTreeIcon type={contentType as ContentTreeTypes} label="" color={iconColor} />
			</Box>
		);
	} else if (isValidEmojiOrFavicon(displayEmoji, contentType)) {
		return (
			<LazyEmojiComponentLoader
				emoji={displayEmoji}
				height={16}
				context="spaceViews"
				wrapper={isNav4Enabled ? Nav4EmojiWrapper : EmojiWrapper}
				contentType={contentType}
			/>
		);
	} else {
		return null;
	}
};

const HIGHLIGHT_FADE = keyframes({
	from: {
		background: token('color.background.accent.blue.subtlest'),
	},
	to: {
		background: 'none',
	},
});

const unboundedStyles = unboundedCssMap({
	root: {
		textDecoration: 'none',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		minHeight: '36px',
		backgroundColor: 'transparent',
		height: '36px',
		paddingBlock: token('space.075'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		span: {
			columnGap: '0px',
		},
		color: token('color.text'),
		'&::before': {
			content: 'none',
		},
		'&:visited, &:active, &:focus, &:hover': {
			color: token('color.text.subtle'),
			textDecoration: 'none',
		},
		'&:active, &:focus': {
			textDecoration: 'none',
		},
		'&:hover': {
			color: token('color.text'),
		},
		animation: 'none',
		'--display-after-icon': 'none',
		'--opacity-after-icon': '0%',
		'&:hover, &:focus-within': {
			'--display-after-icon': 'flex',
			'--opacity-after-icon': '100%',
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	isForceHoverBackground: {
		'--display-after-icon': 'flex',
		'--opacity-after-icon': '100%',
	},
	isHighlighted: {
		animation: `${HIGHLIGHT_FADE} 2s linear`,
	},
	isQuickActionsFocused: {
		'--display-after-icon': 'flex',
		'--opacity-after-icon': '100%',
	},
	isSelected: {
		backgroundColor: token('color.background.selected'),
		'&:visited, &:active, &:focus, &:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: `${token('color.text.selected')} !important`,
		},
		'&:hover, &:focus-within': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
});

const quickActionsMenuDisplayStylesNav4 = unboundedCssMap({
	displayAndOpacity: {
		'--display-after-icon': 'none',
		'--opacity-after-icon': '0%',
		'&:hover, &:focus-within': {
			'--display-after-icon': 'flex',
			'--opacity-after-icon': '100%',
		},
	},
	isQuickActionsFocused: {
		'--display-after-icon': 'flex',
		'--opacity-after-icon': '100%',
	},
	isForceHoverBackground: {
		'--display-after-icon': 'flex',
		'--opacity-after-icon': '100%',
	},
});

const EmojiWrapper: ComponentType<{ children?: React.ReactNode }> = ({ children }) => {
	return (
		<Inline as="span" xcss={styles.emojiWrapper}>
			{children}
		</Inline>
	);
};

const Nav4EmojiWrapper: ComponentType<{ children?: React.ReactNode }> = ({ children }) => {
	return (
		<Inline as="span" xcss={styles.nav4EmojiWrapper}>
			{children}
		</Inline>
	);
};

export const SpaceViewsListItem: FC<SpaceViewsListItemProps & WrappedComponentProps> = ({
	title,
	url,
	onItemClick,
	isSelected,
	item,
	displayEmoji = '',
	contentType,
	spaceKey,
	isSuperAdmin = false,
	highlight = false,
	isHoverPageCardOptedIn = false,
	closeAllHoverPageCards = () => {},
	setOpenQuickActionsId,
	isQuickActionsOpen,
	isQuickActionsFocused,
	setFocusedQuickActionsId,
	isEditingTitleId,
	setIsEditingTitleId,
	onEnterHoverTarget = () => {},
	onLeaveHoverTarget = () => {},
	onBlurHoverTarget = () => {},
	shouldRenderAfterIcon,
	index,
	renderTitle = ({ title }) => title,
}) => {
	const hoverPageCardTriggerRef = useRef<HTMLDivElement>(null);

	const isNav4Enabled = useIsNav4Enabled();
	const onClick = useCallback(() => {
		onItemClick && onItemClick(item.id, index);
		closeAllHoverPageCards();
	}, [onItemClick, item.id, index, closeAllHoverPageCards]);

	const iconColor = isSelected ? token('color.icon.selected') : undefined;

	const isContentTreeIconVisible =
		contentType && includesValidContentTreeType(contentType, isNav4Enabled);
	const isLiveDoc = contentType === 'page' && item.data.subType === 'live';
	const iconContentType = isLiveDoc ? 'liveDoc' : contentType;

	if (isNav4Enabled) {
		return (
			<div
				ref={hoverPageCardTriggerRef}
				onBlur={onBlurHoverTarget}
				onMouseEnter={() =>
					onEnterHoverTarget({
						itemID: item.id.toString(),
						isQuickActionsFocused,
						hoverPageCardTriggerRef,
					})
				}
				onMouseLeave={onLeaveHoverTarget}
				onFocus={() =>
					onEnterHoverTarget({
						itemID: item.id.toString(),
						isQuickActionsFocused,
						hoverPageCardTriggerRef,
					})
				}
				css={[
					quickActionsMenuDisplayStylesNav4.displayAndOpacity,
					isQuickActionsOpen && quickActionsMenuDisplayStylesNav4.isForceHoverBackground,
					isQuickActionsFocused && quickActionsMenuDisplayStylesNav4.isQuickActionsFocused,
				]}
				role="listitem"
			>
				<HoverPreloader
					name="space-views-list-item"
					preloadUrl={url}
					preloadOptions={PreloadingPresets.onlyJS}
				>
					<LinkMenuItem
						testId="space-views-list-item"
						onClick={onClick}
						href={url}
						isSelected={isSelected}
						elemBefore={<NodeIcon label="" />}
						elemAfter={
							shouldRenderAfterIcon && (
								<PageTreeAfterIcon
									setFocusedQuickActionsId={setFocusedQuickActionsId}
									isSuperAdmin={isSuperAdmin}
									isQuickActionsOpen={isQuickActionsOpen}
									setOpenQuickActionsId={setOpenQuickActionsId}
									setIsEditingTitleId={setIsEditingTitleId}
									closeAllHoverPageCards={closeAllHoverPageCards}
									spaceKey={spaceKey}
									item={item}
									displayTitle={title}
									hideFolder
									disableRename
								/>
							)
						}
					>
						<Grid xcss={styles.grid}>
							<EmojiOrContentIcon
								isContentTreeIconVisible={isContentTreeIconVisible}
								contentType={iconContentType}
								iconColor={iconColor}
								displayEmoji={displayEmoji}
								isNav4Enabled
							/>
							<Box as="span" xcss={styles.title}>
								{renderTitle({ title })}
							</Box>
						</Grid>
					</LinkMenuItem>
				</HoverPreloader>
			</div>
		);
	}

	return (
		<div
			ref={hoverPageCardTriggerRef}
			onBlur={onBlurHoverTarget}
			onMouseEnter={() =>
				onEnterHoverTarget({
					itemID: item.id.toString(),
					isQuickActionsFocused,
					hoverPageCardTriggerRef,
				})
			}
			onMouseLeave={onLeaveHoverTarget}
			onFocus={() =>
				onEnterHoverTarget({
					itemID: item.id.toString(),
					isQuickActionsFocused,
					hoverPageCardTriggerRef,
				})
			}
			role="listitem"
		>
			<HoverPreloader
				name="space-views-list-item"
				preloadUrl={url}
				preloadOptions={PreloadingPresets.onlyJS}
			>
				<ConditionalInlineRename
					id={item.id}
					displayEmoji={displayEmoji}
					title={title}
					isEditingTitle={isEditingTitleId === item.id}
					setIsEditingTitleId={setIsEditingTitleId}
					setFocusedQuickActionsId={setFocusedQuickActionsId}
					updateContentTreeItem={noop}
				>
					<ConditionalTreeItemTooltip
						title={title}
						isHoverPageCardOptedIn={isHoverPageCardOptedIn}
						isQuickActionsOpen={isQuickActionsOpen}
					>
						<LinkItem
							testId="space-views-list-item"
							css={[
								unboundedStyles.root,
								isSelected && unboundedStyles.isSelected,
								isQuickActionsOpen && unboundedStyles.isForceHoverBackground,
								highlight && unboundedStyles.isHighlighted,
								isQuickActionsFocused && unboundedStyles.isQuickActionsFocused,
							]}
							onClick={onClick}
							// @ts-ignore
							href={url}
							isSelected={isSelected}
							iconAfter={
								shouldRenderAfterIcon && (
									<PageTreeAfterIcon
										setFocusedQuickActionsId={setFocusedQuickActionsId}
										isSuperAdmin={isSuperAdmin}
										isQuickActionsOpen={isQuickActionsOpen}
										setOpenQuickActionsId={setOpenQuickActionsId}
										setIsEditingTitleId={setIsEditingTitleId}
										closeAllHoverPageCards={closeAllHoverPageCards}
										spaceKey={spaceKey}
										item={item}
										displayTitle={title}
										hideFolder
										disableRename
									/>
								)
							}
							iconBefore={
								<>
									<NodeIcon label="" />
									<EmojiOrContentIcon
										isContentTreeIconVisible={isContentTreeIconVisible}
										contentType={iconContentType}
										iconColor={iconColor}
										displayEmoji={displayEmoji}
									/>
								</>
							}
						>
							{renderTitle({ title })}
						</LinkItem>
					</ConditionalTreeItemTooltip>
				</ConditionalInlineRename>
			</HoverPreloader>
		</div>
	);
};

const noop = () => {};
